import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, LogIn } from 'lucide-react';
import { ShopLogo } from './ShopLogo';

/**
 * WelcomeGate (Jose 2026-10-09) — the first thing EVERYONE sees when they land on the SLIK website.
 * Covers the ground so we don't miss anyone: Fresha is being discontinued (Oct 19) → sign up to be
 * remembered. Same effect everywhere. Shown once per browsing session; the "Sign in / Create account"
 * CTA goes straight to the portal (so it doesn't double up with the /login page), and "Book as a guest"
 * dismisses it. Light-brown trim + the readable standard font sizes.
 */

const SEEN_KEY = 'slik_welcome_gate_v1';
const PORTAL = 'https://my.slikbarberlounge.com';

// Don't interrupt on pages that already carry the message or are utility pages.
const EXCLUDED = ['/app', '/login', '/privacy', '/terms', '/claim', '/review', '/confirm', '/connect', '/manage'];
const isExcluded = (p: string) => EXCLUDED.some((x) => p === x || p.startsWith(x + '/'));

export const WelcomeGate: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // On the WALK-IN page the person is already here to grab a chair — sending them to guest booking would
  // pull them OUT of the walk-in flow (Jose 2026-10-27). Keep the account encouragement, but drop the
  // "Book as a guest" link and give them a plain "maybe later" that just closes and continues the walk-in.
  const isWalkIn = pathname.startsWith('/walkin');

  useEffect(() => {
    try {
      if (isExcluded(pathname) || window.sessionStorage.getItem(SEEN_KEY) === '1') { setOpen(false); return; }
      setOpen(true);
    } catch { setOpen(false); }
  }, [pathname]);

  const close = () => {
    try { window.sessionStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={close}>
      <div
        className="w-full max-w-md bg-white rounded-3xl border-[3px] border-[#D8BAA7] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fresha warning strip — same message everywhere, so nobody's caught off guard */}
        <div className="bg-red-50 border-b border-red-200 px-5 py-3 text-center">
          <span className="text-sm font-black uppercase tracking-wide text-red-600">⚠️ Fresha has been discontinued</span>
        </div>

        <div className="p-6 text-center relative">
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>

          <ShopLogo className="w-16 h-16 object-contain rounded-2xl mx-auto mb-3" />
          <h2 className="text-2xl font-extrabold text-gray-900">Your SLIK Account</h2>
          <p className="mt-2 text-base text-gray-700 leading-snug">
            Sign up now to <b>be remembered</b> — your cuts, rewards &amp; family, all in one place. Then save SLIK to your home screen.
          </p>

          <a
            href={PORTAL}
            className="mt-4 flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-gradient-to-r from-slik-navy to-slik-purple text-white font-extrabold text-lg shadow-button hover:opacity-90 transition-all"
          >
            <LogIn className="w-5 h-5" /> Sign in / Create account
          </a>
          <p className="mt-3 text-sm text-gray-500">Been here before? <b>Claim your account</b> brings your history + rewards.</p>

          <div className="mt-4 pt-4 border-t border-gray-100">
            {isWalkIn ? (
              <button onClick={close} className="text-base font-semibold text-gray-600 hover:text-gray-800">
                Maybe later — <span className="text-slik-purple underline">continue to the open chair →</span>
              </button>
            ) : (
              <button onClick={() => { close(); navigate('/book'); }} className="text-sm text-gray-600">
                Just want to book quickly?{' '}
                <span className="text-slik-purple font-semibold underline">Book as a guest</span>{' '}
                <span className="text-gray-400">(we won’t save your info)</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeGate;
